import React, { useState, useEffect } from 'react';
import { generateCartaExplicacion } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { FileText, Loader2, Download } from 'lucide-react';

export function CartaExplicacionGenerator({ mbl, shipment, containers }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const containerNumbers = (containers || [])
    .map(c => c.container_number)
    .filter(Boolean);

  const defaultRegimen = shipment?.importer_name?.toUpperCase()?.includes('TJSD')
    ? 'Importación a Depósito Fiscal'
    : 'Importación definitiva';

  const [config, setConfig] = useState({
    carta_date: today,
    invoice_no: '',
    invoice_date: today,
    container_numbers: containerNumbers,
    puerto_destino: 'Manzanillo',
    regimen_aduanero: defaultRegimen,
  });

  useEffect(() => {
    if (open && containers) {
      const nums = (containers || []).map(c => c.container_number).filter(Boolean);
      setConfig(prev => ({
        ...prev,
        container_numbers: nums,
        regimen_aduanero: shipment?.importer_name?.toUpperCase()?.includes('TJSD')
          ? 'Importación a Depósito Fiscal'
          : prev.regimen_aduanero,
      }));
    }
  }, [open, containers, shipment]);

  const handleGenerate = async () => {
    if (!config.invoice_no.trim()) {
      toast.error(t('documents.invoiceNoRequired'));
      return;
    }
    setGenerating(true);
    try {
      const res = await generateCartaExplicacion(mbl, config);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CARTA_EXPLICACION_${mbl}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('documents.cartaGenerated'));
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('documents.errorGenerating'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="generate-carta-btn">
          <FileText size={14} className="mr-1.5" />
          {t('documents.generateCarta')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('documents.generateCarta')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-2 rounded bg-muted/30 border border-border/50 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{shipment?.importer_name}</span>
            {' → '}
            <span>{shipment?.supplier_name}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('documents.cartaDate')}</Label>
              <Input
                type="date"
                value={config.carta_date}
                onChange={e => setConfig(p => ({ ...p, carta_date: e.target.value }))}
                className="mt-1"
                data-testid="carta-date-input"
              />
            </div>
            <div>
              <Label>{t('documents.invoiceNo')} *</Label>
              <Input
                value={config.invoice_no}
                onChange={e => setConfig(p => ({ ...p, invoice_no: e.target.value }))}
                placeholder="INV-2026-001"
                className="mt-1"
                data-testid="carta-invoice-no-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('documents.invoiceDate')}</Label>
              <Input
                type="date"
                value={config.invoice_date}
                onChange={e => setConfig(p => ({ ...p, invoice_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('documents.destinationPort')}</Label>
              <Input
                value={config.puerto_destino}
                onChange={e => setConfig(p => ({ ...p, puerto_destino: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>{t('documents.customsRegime')}</Label>
            <Select
              value={config.regimen_aduanero}
              onValueChange={v => setConfig(p => ({ ...p, regimen_aduanero: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="carta-regimen-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Importación definitiva">Importación definitiva</SelectItem>
                <SelectItem value="Importación a Depósito Fiscal">Importación a Depósito Fiscal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t('documents.containers')}</Label>
            <div className="mt-1 flex flex-wrap gap-1">
              {config.container_numbers.map((cn, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-mono">
                  {cn}
                </span>
              ))}
              {config.container_numbers.length === 0 && (
                <span className="text-xs text-muted-foreground">{t('common.none')}</span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="carta-download-btn"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
              {t('documents.downloadDOCX')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
